// Matrix Academy (/academy): where traders learn and practice, apart from the journal. It has its
// own top bar (AcademyTopBar, used by Journal.js instead of the journal's sidebar and top bar) with
// Home (AcademyHome.js), My progress (the same file, page 'progress'), Courses (Zero to Trader),
// Chart Lab (the practice account, with drills and exam days) and Edge Lab (Backtest), and a search
// over the course's lessons. Admin only for now (gold), like the pages in it.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { LanguageSwitch, useI18n } from './i18n';
import { Icon } from './Icons';
import { MODULES } from './course/content';

export const ACADEMY_VIEWS = ['academy', 'academy-progress', 'course', 'practice', 'backtest'];
const TABS = [
  { to: '/academy/progress', label: { en: 'My progress', es: 'Mi progreso' }, icon: 'insights' },
  { to: '/academy/course', label: { en: 'Courses', es: 'Cursos' }, icon: 'book' },
  { to: '/academy/chart-lab', label: { en: 'Chart Lab', es: 'Chart Lab' }, icon: 'chart' },
  { to: '/academy/edge-lab', label: { en: 'Edge Lab', es: 'Edge Lab' }, icon: 'target' },
];

export function AcademyMark() {
  return (
    <span className="academy-mark" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9l10-5 10 5-10 5z" /><path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" /><path d="M22 9v5" />
      </svg>
    </span>
  );
}

// Search the lessons by title (or module), in the trader's language; a pick opens the lesson.
function LessonSearch() {
  const { lang } = useI18n();
  const L = (x) => (x && typeof x === 'object' ? x[lang] || x.en : x || '');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  const navigate = useNavigate();
  const all = useMemo(() => MODULES.filter((m) => !m.soon).flatMap((m) => m.lessons.map((l) => ({ id: l.id, title: L(l.title), module: L(m.title) }))), [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const fold = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const results = q.trim().length < 2 ? [] : all.filter((x) => fold(`${x.title} ${x.module}`).includes(fold(q.trim()))).slice(0, 8);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const go = (id) => { setOpen(false); setQ(''); navigate(`/academy/course?lesson=${id}`); };
  return (
    <div className="academy-search" ref={box}>
      <button type="button" className="academy-icon-btn" aria-label={L({ en: 'Search lessons', es: 'Buscar lecciones' })} aria-expanded={open} onClick={() => setOpen((x) => !x)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      </button>
      {open && (
        <div className="academy-search-pop">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={L({ en: 'Search lessons…', es: 'Buscar lecciones…' })} aria-label={L({ en: 'Search lessons', es: 'Buscar lecciones' })}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && results[0]) go(results[0].id); }} />
          {q.trim().length >= 2 && (results.length ? (
            <ul>
              {results.map((x) => <li key={x.id}><button type="button" onClick={() => go(x.id)}><b>{x.title}</b><span>{x.module}</span></button></li>)}
            </ul>
          ) : <p className="field-hint">{L({ en: 'No lessons match.', es: 'Ninguna lección coincide.' })}</p>)}
        </div>
      )}
    </div>
  );
}

export function AcademyTopBar() {
  const { t, lang } = useI18n();
  const L = (x) => (x && typeof x === 'object' ? x[lang] || x.en : x || '');
  return (
    <header className="academy-topbar">
      <Link to="/academy" className="academy-brand">
        <AcademyMark />
        <span>{t('Matrix Academy')}</span>
      </Link>
      <nav className="academy-tabs" aria-label={t('Matrix Academy')}>
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className={({ isActive }) => `academy-tab${isActive ? ' active' : ''}`}><Icon name={tab.icon} />{L(tab.label)}</NavLink>
        ))}
      </nav>
      <div className="academy-topbar-right">
        <LessonSearch />
        <LanguageSwitch />
        <Link to="/app/home" className="academy-back">← <span className="academy-back-full">{L({ en: 'Back to Journal', es: 'Volver al diario' })}</span><span className="academy-back-short">{t('Journal')}</span></Link>
      </div>
    </header>
  );
}
